import { Component } from '@theme/component';


const CLEAROUT_READY_TIMEOUT_MS = 4000;

// Must exceed Clearout's own `timeout` option
const CLEAROUT_VALIDATION_TIMEOUT_MS = 11000;

/**
 * A custom element that handles email signup form submission with Klaviyo integration.
 *
 * @extends {Component}
 */
class EmailSignupFormComponent extends Component {
  /** @type {boolean} */
  isSubmitting = false;

  /** @type {boolean} */
  hasBlurredEmail = false;

  /** @type {boolean} */
  #submitListenerAttached = false;

  /** @type {((event: Event) => void) | null} */
  #boundClearoutInit = null;

  /** @type {(() => void) | null} */
  #boundClearoutLoading = null;

  /** @type {boolean} */
  #clearoutReady = false;

  /** @type {Promise<void> | null} */
  #clearoutReadyPromise = null;

  /** @type {(() => void) | null} */
  #resolveClearoutReady = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #clearoutReadyTimeoutId = null;

  /** @type {MutationObserver | null} */
  #cofeedbackObserver = null;

   /** @type {string | null} */
  #lastCleanValidatedEmail = null;

  /** @type {MutationObserver | null} */
  #formSubtreeObserver = null;


  /** @type {((event: Event) => void) | null} */
  #boundSubmit = null;

  /** @type {(() => void) | null} */
  #boundEmailInputState = null;

  /** @type {(() => void) | null} */
  #boundEmailBlurState = null;

  /** @type {string | undefined} */
  klaviyoListId;

  /** @type {string | undefined} */
  klaviyoCompanyId;

  /** @type {string | undefined} */
  blockId;

  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {HTMLInputElement | null} */
  emailInput = null;

  /** @type {HTMLButtonElement | null} */
  submitButton = null;

  connectedCallback() {
    super.connectedCallback();

    this.form = this.querySelector('form');
    if (!this.form) return;

    this.emailInput = this.form.querySelector('input[type="email"]');
    this.submitButton = this.form.querySelector('button[type="submit"]');

    this.klaviyoListId = this.dataset.klaviyoListId;
    this.klaviyoCompanyId = this.dataset.klaviyoCompanyId;
    this.blockId = this.dataset.blockId;

    if (this.klaviyoCompanyId) {
      this.setAttribute('data-klaviyo-enabled', 'true');
    }

    this.#boundSubmit = this.#handleSubmit.bind(this);
    this.#attachSubmitListener();
    this.#boundEmailInputState = this.#handleEmailInputState.bind(this);
    this.#boundEmailBlurState = this.#handleEmailBlurState.bind(this);
    this.#boundClearoutInit = this.#handleClearoutInitialized.bind(this);
    this.#boundClearoutLoading = this.#handleClearoutLoading.bind(this);
    if (this.emailInput && this.#boundEmailInputState) {
      this.emailInput.addEventListener('input', this.#boundEmailInputState);
    }
    if (this.emailInput && this.#boundEmailBlurState) {
      this.emailInput.addEventListener('blur', this.#boundEmailBlurState);
    }
    this.#clearoutReadyPromise = new Promise((resolve) => {
      this.#resolveClearoutReady = resolve;
    });
    if (this.form) {
      this.form.addEventListener('clearout:initialized', this.#boundClearoutInit, { once: true });
      this.form.addEventListener('clearout:loading', this.#boundClearoutLoading, { once: true });
    }
    if (window.__clearoutState) {
      this.#markClearoutReady();
    }
    this.#updateSubmitDisabledState();
    this.#syncClearoutFeedbackVisibility();
    this.#watchForCofeedback();

    requestAnimationFrame(() => {
      if (this.klaviyoCompanyId) {
        this.#hideMessages();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.form && this.#boundSubmit) {
      this.form.removeEventListener('submit', this.#boundSubmit, false);
    }
    if (this.emailInput && this.#boundEmailInputState) {
      this.emailInput.removeEventListener('input', this.#boundEmailInputState);
    }
    if (this.emailInput && this.#boundEmailBlurState) {
      this.emailInput.removeEventListener('blur', this.#boundEmailBlurState);
    }
    if (this.form && this.#boundClearoutInit) {
      this.form.removeEventListener('clearout:initialized', this.#boundClearoutInit);
    }
    if (this.form && this.#boundClearoutLoading) {
      this.form.removeEventListener('clearout:loading', this.#boundClearoutLoading);
    }
    if (this.#clearoutReadyTimeoutId !== null) {
      clearTimeout(this.#clearoutReadyTimeoutId);
      this.#clearoutReadyTimeoutId = null;
    }
    this.#cofeedbackObserver?.disconnect();
    this.#cofeedbackObserver = null;
    this.#formSubtreeObserver?.disconnect();
    this.#formSubtreeObserver = null;
    this.#submitListenerAttached = false;
  }

  /**
   * Watches the form's subtree for the Clearout `.cofeedback` element to appear.
   * As soon as it exists, starts the cofeedback observer immediately — no dependency
   * on the clearout:initialized event timing.
   */
  #watchForCofeedback() {
    if (!this.form) return;

    // If cofeedback is already in the DOM (e.g. Clearout loaded before us), start now.
    if (this.form.querySelector('.cofeedback')) {
      this.#startCofeedbackObserver();
      return;
    }

    this.#formSubtreeObserver = new MutationObserver(() => {
      if (this.form?.querySelector('.cofeedback')) {
        this.#formSubtreeObserver?.disconnect();
        this.#formSubtreeObserver = null;
        this.#startCofeedbackObserver();
      }
    });

    this.#formSubtreeObserver.observe(this.form, { childList: true, subtree: true });
  }

  /**
   * Re-attaches the submit listener after Clearout has loaded and registered its own listener.
   * This guarantees Clearout's listener fires before ours, so it can block invalid submissions.
   * @param {Event} event - The clearout:initialized event.
   */
  #handleClearoutInitialized(event) {
    if (!this.form || !this.#boundSubmit) return;
    this.form.removeEventListener('submit', this.#boundSubmit, false);
    this.#submitListenerAttached = false;
    this.#attachSubmitListener();
    const detail = /** @type {CustomEvent<{loaded: boolean}>} */ (event).detail;
    if (detail?.loaded !== false) {
      this.#startCofeedbackObserver();
    }
    this.#markClearoutReady();
  }

  #handleClearoutLoading() {
    if (this.#clearoutReadyTimeoutId !== null) return;
    this.#clearoutReadyTimeoutId = setTimeout(() => this.#markClearoutReady(), CLEAROUT_READY_TIMEOUT_MS);
  }

  #markClearoutReady() {
    this.#clearoutReady = true;
    this.#resolveClearoutReady?.();
    this.#resolveClearoutReady = null;
    if (this.#clearoutReadyTimeoutId !== null) {
      clearTimeout(this.#clearoutReadyTimeoutId);
      this.#clearoutReadyTimeoutId = null;
    }
  }

  /**
   * Registers submit listener on bubble phase so Clearout can prevent invalid submissions first.
   */
  #attachSubmitListener() {
    if (this.#submitListenerAttached || !this.form || !this.#boundSubmit) return;
    this.#submitListenerAttached = true;
    // Bubble phase so Clearout can validate before we intercept Klaviyo POST.
    this.form.addEventListener('submit', this.#boundSubmit, false);
  }

  /**
   * Handles form submission.
   * @param {Event} event - The submit event.
   */
  async #handleSubmit(event) {
    if (event.defaultPrevented) {
      return;
    }

    if (this.isSubmitting) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!this.klaviyoCompanyId) {
      return;
    }

    if (!this.emailInput) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const email = this.emailInput.value?.trim();

    if (!this.emailInput.validity.valid) {
      this.hasBlurredEmail = true;
      this.#updateSubmitDisabledState();
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!email || !this.#isValidEmail(email)) {
      this.hasBlurredEmail = true;
      this.#updateSubmitDisabledState();
      this.#showValidationError();
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.isSubmitting = true;
    this.#setButtonLoading(true);
    this.#hideMessages();

    try {
      if (!this.#clearoutReady && this.#clearoutReadyPromise) {
        this.#handleClearoutLoading();
        await this.#clearoutReadyPromise;
      }

      const clearoutResponded = await this.#awaitClearoutValidation();

      if (!clearoutResponded && this.#isClearoutAvailable()) {
        this.#showError();
        return;
      }

      if (clearoutResponded && this.#hasClearoutValidationError()) {
        this.#showValidationError();
        return;
      }

      const success = await this.#submitToKlaviyo(email);
      if (success) {
        this.#showSuccess();
        this.form?.reset();
        // form.reset() doesn't fire input events, so manually restore initial state.
        this.hasBlurredEmail = false;
        this.#syncClearoutFeedbackVisibility();
        this.#updateSubmitDisabledState();
      } else {
        this.#showError();
        return;
      }
    } catch (error) {
      this.#showError();
      return;
    } finally {
      this.isSubmitting = false;
      this.#setButtonLoading(false);
    }
  }

  /**
   * Submits email to Klaviyo API.
   * @param {string} email - The email address.
   * @returns {Promise<boolean>} Whether the submission was successful.
   */
  async #submitToKlaviyo(email) {
    if (!this.klaviyoCompanyId) {
      return false;
    }

    /** @type {any} */
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
      },
    };

    if (this.klaviyoListId) {
      payload.data.relationships = {
        list: {
          data: {
            type: 'list',
            id: this.klaviyoListId,
          },
        },
      };
    }

    const apiUrl = `https://a.klaviyo.com/client/subscriptions?company_id=${this.klaviyoCompanyId}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          revision: '2026-01-15',
          'content-type': 'application/vnd.api+json',
        },
        body: JSON.stringify(payload),
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Validates email format.
   * @param {string} email - The email address to validate.
   * @returns {boolean} Whether the email is valid.
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }


  /**
   * Returns a form message element by ID using a selector-safe lookup.
   * @param {string} elementId - The element ID to resolve.
   * @returns {HTMLElement | null}
   */
  #getMessageElementById(elementId) {
    if (!this.form || !elementId) return null;

    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return /** @type {HTMLElement | null} */ (this.form.querySelector(`#${CSS.escape(elementId)}`));
    }

    return /** @type {HTMLElement | null} */ (this.form.querySelector(`[id="${elementId}"]`));
  }

  /**
   * Shows success message.
   */
  #showSuccess() {
    const successMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-success-${this.blockId}`
    );
    const klaviyoErrorMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-error-${this.blockId}`
    );
    const shopifySuccessMessage = this.#getMessageElementById(
      `Email-signup__message-success-${this.blockId}`
    );

    if (klaviyoErrorMessage) {
      klaviyoErrorMessage.hidden = true;
    }

    if (successMessage) {
      successMessage.hidden = false;
      successMessage.classList.remove('email-signup__message--hidden');
      successMessage.focus();
      this.emailInput?.setAttribute('aria-describedby', successMessage.id);
    }

    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
  }

  /**
   * Shows validation error message.
   */
  #showValidationError() {
    const klaviyoErrorMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-error-${this.blockId}`
    );
    const klaviyoSuccessMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-success-${this.blockId}`
    );
    const shopifySuccessMessage = this.#getMessageElementById(
      `Email-signup__message-success-${this.blockId}`
    );

    if (klaviyoSuccessMessage) {
      klaviyoSuccessMessage.hidden = true;
    }

    if (klaviyoErrorMessage) {
      const messageText = klaviyoErrorMessage.querySelector('.email-signup__message-text');
      if (messageText) {
        const validationText = messageText.getAttribute('data-validation-text');
        if (validationText) {
          messageText.textContent = validationText;
        }
      }
      klaviyoErrorMessage.hidden = false;
      klaviyoErrorMessage.classList.remove('email-signup__message--hidden');
      klaviyoErrorMessage.focus();
      this.emailInput?.setAttribute('aria-invalid', 'true');
      this.emailInput?.setAttribute('aria-describedby', klaviyoErrorMessage.id);
    }

    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
  }

  /**
   * Shows error message.
   */
  #showError() {
    const errorMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-error-${this.blockId}`
    );
    const klaviyoSuccessMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-success-${this.blockId}`
    );
    const shopifySuccessMessage = this.#getMessageElementById(
      `Email-signup__message-success-${this.blockId}`
    );

    if (klaviyoSuccessMessage) {
      klaviyoSuccessMessage.hidden = true;
    }

    if (errorMessage) {
      const messageText = errorMessage.querySelector('.email-signup__message-text');
      if (messageText) {
        const defaultText = messageText.getAttribute('data-default-text');
        if (defaultText) {
          messageText.textContent = defaultText;
        }
      }
      errorMessage.hidden = false;
      errorMessage.classList.remove('email-signup__message--hidden');
      errorMessage.focus();
      this.emailInput?.setAttribute('aria-describedby', errorMessage.id);
    }

    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
  }

  /**
   * Hides all messages.
   */
  #hideMessages() {
    const klaviyoSuccessMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-success-${this.blockId}`
    );
    const klaviyoErrorMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-error-${this.blockId}`
    );
    const shopifySuccessMessage = this.#getMessageElementById(
      `Email-signup__message-success-${this.blockId}`
    );
    const shopifyErrorMessage = this.#getMessageElementById(
      `Email-signup__message-error-${this.blockId}`
    );

    if (klaviyoSuccessMessage) klaviyoSuccessMessage.hidden = true;
    if (klaviyoErrorMessage) klaviyoErrorMessage.hidden = true;
    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
    if (shopifyErrorMessage) shopifyErrorMessage.hidden = true;

    this.emailInput?.removeAttribute('aria-invalid');
    this.emailInput?.removeAttribute('aria-describedby');
  }

  /**
   * Hides only Klaviyo-specific messages and input aria state.
   */
  #hideKlaviyoMessages() {
    const klaviyoSuccessMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-success-${this.blockId}`
    );
    const klaviyoErrorMessage = this.#getMessageElementById(
      `Email-signup__message-klaviyo-error-${this.blockId}`
    );

    if (klaviyoSuccessMessage) klaviyoSuccessMessage.hidden = true;
    if (klaviyoErrorMessage) klaviyoErrorMessage.hidden = true;

    this.emailInput?.removeAttribute('aria-invalid');
    this.emailInput?.removeAttribute('aria-describedby');
  }

  /**
   * Syncs the data-signup-email-empty attribute and re-evaluates cofeedback visibility.
   */
  #syncClearoutFeedbackVisibility() {
    if (!this.form) return;
    const email = this.emailInput?.value?.trim() ?? '';
    this.form.toggleAttribute('data-signup-email-empty', !email);
    this.#applyCofeedbackVisibility();
  }

  /**
   * Hides the Clearout cofeedback unless it contains actual error text beyond the
   * "Powered by Clearout.io" branding. Also always hides when the email field is empty.
   * This is called both proactively (on input/blur) and reactively (from the observer).
   */
  #applyCofeedbackVisibility() {
    const cofeedback = this.form?.querySelector('.cofeedback');
    if (!cofeedback) return;

    const email = this.emailInput?.value?.trim() ?? '';
    const hasRealError = this.#cofeedbackHasRealError(cofeedback);
    const shouldShow = !!email && hasRealError;
    if (shouldShow) {
      cofeedback.style.removeProperty('display');
    } else {
      cofeedback.style.setProperty('display', 'none', 'important');
    }

    if (email && !hasRealError) {
      this.#lastCleanValidatedEmail = email;
    }
  }

  /**
   * @param {Element} cofeedback - The Clearout `.cofeedback` element.
   * @returns {boolean} Whether it contains error text beyond the "Powered by Clearout.io" branding.
   */
  #cofeedbackHasRealError(cofeedback) {
    const raw = cofeedback.textContent ?? '';
    const withoutBranding = raw.replace(/powered\s+by\s+clearout\.?io/gi, '').trim();
    return withoutBranding.length > 0;
  }

  /**
   * @returns {Promise<boolean>} Whether Clearout actually responded before the hard timeout,
   *   or had already confirmed a clean result for the exact current email prior to this call.
   *   `false` means validation is genuinely inconclusive (Clearout never updated `.cofeedback`
   *   for this email and there was no prior clean result for it to fall back on), so the
   *   caller must not treat that as "no error found".
   */
  #awaitClearoutValidation() {
    const cofeedback = this.form?.querySelector('.cofeedback');
    const emailInput = this.emailInput;
    if (!cofeedback || !emailInput) return Promise.resolve(false);

    const currentEmail = emailInput.value?.trim() ?? '';
    // Only trust a pre-existing clean `.cofeedback` state if it was actually observed for
    // this exact email. Otherwise it may be stale (leftover from a prior email, or the
    // untouched initial state) and must not be treated as validation of the current value.
    const alreadyClean = !!currentEmail && currentEmail === this.#lastCleanValidatedEmail;

    return new Promise((resolve) => {
      let settled = false;
      let quietTimeoutId = /** @type {ReturnType<typeof setTimeout> | null} */ (null);

      const finish = (/** @type {boolean} */ responded) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        clearTimeout(hardTimeoutId);
        if (quietTimeoutId !== null) clearTimeout(quietTimeoutId);
        resolve(responded);
      };

      const observer = new MutationObserver(() => {
        if (quietTimeoutId !== null) clearTimeout(quietTimeoutId);
        quietTimeoutId = setTimeout(() => finish(true), 150);
      });
      observer.observe(cofeedback, { childList: true, subtree: true, characterData: true });

      const hardTimeoutId = setTimeout(() => finish(alreadyClean), CLEAROUT_VALIDATION_TIMEOUT_MS);

      emailInput.focus();
      emailInput.blur();
    });
  }

  #hasClearoutValidationError() {
    const cofeedback = this.form?.querySelector('.cofeedback');
    if (!cofeedback) return false;
    return this.#cofeedbackHasRealError(cofeedback);
  }

  #isClearoutAvailable() {
    return window.__clearoutState?.loaded !== false;
  }

  /**
   * Starts a permanent MutationObserver on the cofeedback element so that any time
   * Clearout updates its content we immediately re-evaluate whether to show or hide it.
   */
  #startCofeedbackObserver() {
    const cofeedback = this.form?.querySelector('.cofeedback');
    if (!cofeedback) return;

    this.#cofeedbackObserver?.disconnect();

    // Apply immediately so the branding-only state is hidden from the start.
    this.#applyCofeedbackVisibility();

    this.#cofeedbackObserver = new MutationObserver((mutations) => {
      // Re-evaluate cofeedback visibility whenever Clearout updates it.
      this.#applyCofeedbackVisibility();

      // If Clearout disables the button while the field is empty and we're not
      // submitting, re-enable it (stale validation result).
      for (const m of mutations) {
        if (
          m.target === this.submitButton &&
          m.attributeName === 'disabled' &&
          this.submitButton?.disabled &&
          !this.isSubmitting &&
          !this.hasBlurredEmail &&
          !this.emailInput?.value?.trim()
        ) {
          this.submitButton.disabled = false;
        }
      }
    });

    this.#cofeedbackObserver.observe(cofeedback, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    // Also watch the submit button for Clearout disabling it after a stale RPC result.
    if (this.submitButton) {
      this.#cofeedbackObserver.observe(this.submitButton, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }
  }

  /**
   * Updates submit state on email input.
   */
  #handleEmailInputState() {
    const email = this.emailInput?.value?.trim() ?? '';

    // Always dismiss all messages (Klaviyo and Shopify native) the moment the user types anything.
    this.#hideMessages();

    if (!email) {
      this.hasBlurredEmail = false;
    }

    this.#lastCleanValidatedEmail = null;

    this.#syncClearoutFeedbackVisibility();
    this.#updateSubmitDisabledState();
  }

  /**
   * Enables validation-driven disabled state after first blur.
   * Skips activation when the field is empty so clearing and tabbing away
   * always returns the form to its initial state.
   */
  #handleEmailBlurState() {
    const email = this.emailInput?.value?.trim() ?? '';
    // Always sync cofeedback so data-signup-email-empty is set before Clearout's
    // own blur handler fires and potentially writes to .cofeedback.
    this.#syncClearoutFeedbackVisibility();
    if (!email) return;
    this.hasBlurredEmail = true;
    this.#updateSubmitDisabledState();
  }

  /**
   * Sets button loading state.
   * @param {boolean} loading - Whether the button should be in loading state.
   */
  #setButtonLoading(loading) {
    if (this.submitButton) {
      this.submitButton.disabled = loading || this.#isSubmitDisabledForEmail();
      this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
  }

  /**
   * Keeps submit disabled until a valid email is entered.
   */
  #updateSubmitDisabledState() {
    if (!this.submitButton) return;
    if (this.isSubmitting) return;
    this.submitButton.disabled = this.#isSubmitDisabledForEmail();
  }

  /**
   * @returns {boolean}
   */
  #isSubmitDisabledForEmail() {
    if (!this.emailInput) return true;
    if (!this.hasBlurredEmail) return false;
    const email = this.emailInput.value?.trim() ?? '';
    if (!email) return true;
    return !this.emailInput.validity.valid || !this.#isValidEmail(email);
  }
}

if (!customElements.get('email-signup-form')) {
  customElements.define('email-signup-form', EmailSignupFormComponent);
}
